import React from 'react';
import AdminLayout from '../../layouts/AdminLayout';

type Stats = {
  total_users: number;
  registered_today: number;
  active_7d: number;
  table_counts: Record<string, number>;
  registrations_by_day: { date: string; count: number }[];
};

type OverviewProps = { stats: Stats };
type OverviewComponent = React.FC<OverviewProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const nf = new Intl.NumberFormat('zh-CN');

const Stat: React.FC<{ k: string; v: number; d?: string }> = ({ k, v, d }) => (
  <div className="rounded-lg border border-admin-line bg-admin-surface px-3 py-2.5">
    <div className="mb-0.5 text-[11.5px] text-admin-muted">{k}</div>
    <div className="text-[23px] font-semibold tabular-nums tracking-tight">{nf.format(v)}</div>
    {d && <div className="mt-0.5 text-[11.5px] text-admin-muted">{d}</div>}
  </div>
);

// 柱图用内联 SVG，不引图表库：三十根柱子不值得一个依赖。
const Curve: React.FC<{ days: Stats['registrations_by_day'] }> = ({ days }) => {
  const max = Math.max(1, ...days.map((d) => d.count));
  const W = 620;
  const H = 92;
  const gap = 3;
  const bw = (W - gap * (days.length - 1)) / days.length;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-[92px] w-full">
      {days.map((d, i) => {
        const bh = d.count ? Math.max(3, (d.count / max) * (H - 14)) : 0;
        return (
          <rect
            key={d.date}
            x={i * (bw + gap)}
            y={H - 1 - bh}
            width={bw}
            height={bh}
            rx={1.5}
            className="fill-admin-accent opacity-75"
          >
            <title>{`${d.date} · ${d.count} 位新用户`}</title>
          </rect>
        );
      })}
      <line x1={0} y1={H - 0.5} x2={W} y2={H - 0.5} className="stroke-admin-line" strokeWidth={1} />
    </svg>
  );
};

const Overview: OverviewComponent = ({ stats }) => {
  const total = stats.registrations_by_day.reduce((s, d) => s + d.count, 0);
  const pct = stats.total_users ? Math.round((stats.active_7d / stats.total_users) * 1000) / 10 : 0;

  return (
    <>
      <h1 className="mb-3 text-[14.5px] font-semibold tracking-tight">总览</h1>

      <div className="mb-3.5 grid grid-cols-[repeat(4,minmax(0,1fr))] gap-3">
        <Stat k="总用户" v={stats.total_users} />
        <Stat k="今日新增" v={stats.registered_today} />
        <Stat k="7 日活跃" v={stats.active_7d} d={`${pct}% 用户`} />
        <Stat k="近 30 天注册" v={total} />
      </div>

      <div className="grid grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] items-start gap-3">
        <div className="rounded-lg border border-admin-line bg-admin-surface">
          <div className="flex items-center gap-2 border-b border-admin-line px-3.5 py-2.5 text-[12.5px] font-semibold">
            近 30 天注册
            <span className="font-normal text-admin-muted">每日新增用户</span>
          </div>
          <div className="p-3.5">
            <Curve days={stats.registrations_by_day} />
          </div>
        </div>

        <div className="rounded-lg border border-admin-line bg-admin-surface">
          <div className="border-b border-admin-line px-3.5 py-2.5 text-[12.5px] font-semibold">
            各表行数
          </div>
          {/* minmax(0,1fr) 而不是 1fr：1fr 的最小尺寸是 min-content，
              一个长表名（oauth_access_tokens）就能撑破容器造成横向溢出。 */}
          <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-px bg-admin-line">
            {Object.entries(stats.table_counts).map(([table, n]) => (
              <div key={table} className="bg-admin-surface px-3 py-2.5">
                <div className="truncate text-[11px] text-admin-muted">{table}</div>
                <div className="mt-px text-[15px] font-semibold tabular-nums">{nf.format(n)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

Overview.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;

export default Overview;
